<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BoardFormDialog from './components/BoardFormDialog.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const boards = useMapGetter('kanban/boards/getBoards');
const uiFlags = computed(() => getters['kanban/boards/getUIFlags'].value);

// I7: an unhandled rejection here left the screen showing "no boards yet" after a failed load.
store
  .dispatch('kanban/boards/get')
  .catch(() => useAlert(t('KANBAN.SETTINGS.LOAD_ERROR')));

const dialogRef = ref(null);
const editingBoard = ref(null);

const openCreate = () => {
  editingBoard.value = null;
  dialogRef.value.dialogRef.open();
};

const openEdit = board => {
  editingBoard.value = board;
  dialogRef.value.dialogRef.open();
};

const showDeletePopup = ref(false);
const selectedBoard = ref({});
const loading = ref({});

const openDelete = board => {
  selectedBoard.value = board;
  showDeletePopup.value = true;
};

const confirmDeletion = async () => {
  const { id } = selectedBoard.value;
  loading.value[id] = true;
  showDeletePopup.value = false;
  try {
    await store.dispatch('kanban/boards/delete', id);
    useAlert(t('KANBAN.SETTINGS.BOARD_API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.BOARD_API.DELETE_ERROR'));
  } finally {
    loading.value[id] = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('KANBAN.SETTINGS.LOADING')"
    :no-records-found="!boards.length"
    :no-records-message="t('KANBAN.SETTINGS.NO_BOARDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('KANBAN.SETTINGS.HEADER')"
        :description="t('KANBAN.SETTINGS.DESCRIPTION')"
        feature-name="kanban"
      >
        <template #actions>
          <Button
            v-if="isAdmin"
            :label="t('KANBAN.SETTINGS.NEW_BOARD')"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <router-link
          v-for="board in boards"
          :key="board.id"
          :to="{ name: 'kanban_settings_show', params: { boardId: board.id } }"
          class="flex justify-between items-center gap-4 py-4"
        >
          <div class="flex items-center gap-2">
            <span class="text-heading-3 text-n-slate-12">{{ board.name }}</span>
            <span
              v-if="board.is_default"
              class="px-2 py-0.5 rounded-full bg-n-slate-3 text-xs text-n-slate-11"
            >
              {{ t('KANBAN.SETTINGS.DEFAULT_BADGE') }}
            </span>
          </div>
          <div class="flex gap-2" @click.stop.prevent>
            <Button
              v-if="isAdmin"
              icon="i-woot-settings"
              slate
              sm
              @click="openEdit(board)"
            />
            <Button
              v-if="isAdmin"
              icon="i-woot-bin"
              slate
              sm
              :is-loading="loading[board.id]"
              @click="openDelete(board)"
            />
          </div>
        </router-link>
      </div>
    </template>
    <BoardFormDialog ref="dialogRef" :board="editingBoard" />
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        t('KANBAN.SETTINGS.BOARD_DELETE.CONFIRM_TITLE', {
          boardName: selectedBoard.name,
        })
      "
      :message="t('KANBAN.SETTINGS.BOARD_DELETE.CONFIRM_MESSAGE')"
      :confirm-text="t('KANBAN.SETTINGS.BOARD_DELETE.CONFIRM_YES')"
      :reject-text="t('KANBAN.SETTINGS.BOARD_DELETE.CONFIRM_NO')"
      :confirm-value="selectedBoard.name"
      :confirm-place-holder-text="
        t('KANBAN.SETTINGS.BOARD_DELETE.CONFIRM_PLACEHOLDER')
      "
      @on-confirm="confirmDeletion"
      @on-close="showDeletePopup = false"
    />
  </SettingsLayout>
</template>
